import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { WalletLink } from "./WalletLink";
import { formatCards } from "../utils/format";
import { Skeleton } from "./Skeleton";

interface QuarterlyBreakdownTableProps {
  from: string;
  to: string;
  /** When set, $CARDS received in this airdrop is shown per wallet */
  airdropId?: string;
}

export function QuarterlyBreakdownTable({
  from,
  to,
  airdropId,
}: QuarterlyBreakdownTableProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["quarterly-points", from, to, airdropId ?? null],
    queryFn: async () => {
      const { data, error } = await api.points.quarterly.get({
        query: { from, to, limit: 100, ...(airdropId ? { airdropId } : {}) },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!from && !!to,
  });

  if (isLoading) {
    return <Skeleton kind="table" rows={8} />;
  }

  if (error) {
    return (
      <p className="text-red-400">Could not load quarterly data: {String(error)}</p>
    );
  }

  if (!data?.data || data.data.length === 0) {
    return (
      <p className="text-gray-500 py-4">
        No points data for this period.
      </p>
    );
  }

  const airdrop = data.airdrop;

  const rankClass = (index: number) =>
    index === 0
      ? "bg-yellow-400/15 text-yellow-400"
      : index < 3
      ? "bg-gray-600 text-gray-100"
      : "bg-gray-700 text-gray-400";

  return (
    <div>
      {/* Summary */}
      <div
        className={`mb-4 grid gap-3 ${
          airdrop ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"
        }`}
      >
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">
            Total platform points gained
          </div>
          <div className="text-xl font-semibold text-gray-50 num">
            {data.totalPlatformGain.toLocaleString()}
          </div>
        </div>
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">Wallets ranked</div>
          <div className="text-xl font-semibold text-gray-50 num">
            {data.rankedWallets.toLocaleString()}
          </div>
        </div>
        {airdrop && (
          <>
            <div className="card-inset p-3">
              <div className="text-gray-400 text-xs mb-1">
                {airdrop.label}, $CARDS distributed
              </div>
              <div className="text-xl font-semibold text-yellow-400 num">
                {formatCards(airdrop.totalDistributed)}
              </div>
            </div>
            <div className="card-inset p-3">
              <div className="text-gray-400 text-xs mb-1">
                Airdrop recipients
              </div>
              <div className="text-xl font-semibold text-gray-50 num">
                {airdrop.recipientCount.toLocaleString()}
              </div>
              {airdrop.recipientCount === 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  Not indexed yet
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="pb-3 pr-4 w-16 font-medium">Rank</th>
              <th className="pb-3 pr-4 font-medium">Wallet</th>
              <th className="pb-3 pr-4 text-right font-medium">Points gained</th>
              <th className="pb-3 pr-4 text-right font-medium">Primary</th>
              <th className="pb-3 pr-4 text-right font-medium">Secondary</th>
              <th className="pb-3 pr-4 text-right font-medium">Bonus</th>
              <th className="pb-3 pr-4 text-right font-medium">Share</th>
              {airdrop && (
                <th className="pb-3 text-right font-medium">$CARDS received</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.data.map((wallet, index) => (
              <tr key={wallet.walletAddress} className="table-row">
                <td className="py-3 pr-4">
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold num ${rankClass(
                      index
                    )}`}
                  >
                    {index + 1}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <WalletLink address={wallet.walletAddress} />
                </td>
                <td className="py-3 pr-4 text-right font-semibold text-gray-50 num">
                  {wallet.pointsGained.toLocaleString()}
                </td>
                <td className="py-3 pr-4 text-right text-gray-300 num">
                  {wallet.primaryGained.toLocaleString()}
                </td>
                <td className="py-3 pr-4 text-right text-gray-300 num">
                  {wallet.secondaryGained.toLocaleString()}
                </td>
                <td className="py-3 pr-4 text-right text-gray-300 num">
                  {wallet.bonusGained.toLocaleString()}
                </td>
                <td className="py-3 pr-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-blue-400 h-1.5 rounded-full"
                        style={{
                          width: `${Math.min(wallet.sharePercent, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-gray-200 font-medium w-14 text-right num">
                      {wallet.sharePercent.toFixed(2)}%
                    </span>
                  </div>
                </td>
                {airdrop && (
                  <td className="py-3 text-right font-medium num">
                    {wallet.cardsReceived !== null ? (
                      <span className="text-yellow-400">
                        {formatCards(wallet.cardsReceived)}
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
